
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReportImageButton from "../ReportImageButton";
import ModeratedContentPlaceholder from "@/components/photo/ModeratedContentPlaceholder";
import { shouldShowPlaceholder } from "@/utils/photoModeration";

interface ProfilePhotoCarouselProps {
  photos: string[];
  userName: string;
  userId: string;
}

const ProfilePhotoCarousel = ({ photos, userName, userId }: ProfilePhotoCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!photos || photos.length === 0) {
    return (
      <div className="relative h-80 bg-gray-200 flex items-center justify-center">
        <span className="text-gray-400">No photos available</span>
      </div>
    );
  }

  const nextPhoto = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const currentPhoto = photos[currentIndex];
  const isModerated = shouldShowPlaceholder(currentPhoto);

  return (
    <div className="relative h-80 bg-gray-100 overflow-hidden">
      {isModerated ? (
        <div className="w-full h-full flex items-center justify-center">
          <ModeratedContentPlaceholder 
            size="xl" 
            className="w-full h-full rounded-none" 
            reason="Content moderated for community guidelines"
          />
        </div>
      ) : (
        <img
          src={currentPhoto}
          alt={`${userName} photo ${currentIndex + 1}`}
          className="w-full h-full object-cover"
        />
      )}

      {/* Navigation arrows */}
      {photos.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
            onClick={prevPhoto}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
            onClick={nextPhoto}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}

      {/* Photo indicators */}
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {photos.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === currentIndex ? 'bg-white' : 'bg-white/50'
              }`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}

      {/* Report button - only show for non-moderated content */}
      {!isModerated && (
        <div className="absolute top-2 right-2">
          <ReportImageButton photoUrl={currentPhoto} userId={userId} />
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoCarousel;
